import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT, useLocale } from '../i18n/LanguageContext';
import { clearHelpDrafts } from '../components/HelpComposer';
import HelpWorkspaceView from '../components/HelpWorkspaceView';
import { helpPreviewKey, mergeMessages, helpDate } from '../components/helpPresentation';
import service, { cancelHelpReads } from '../services/helpCenterService';
import { helpIdentity } from '../utils/helpScope';
import { captureSession, isSessionCurrent } from '../utils/authSession';
import './HelpCenter.css';

function HelpWorkspace({ user, identity }) {
  const t = useT();
  const locale = useLocale();
  const admin = user.role === 'admin';
  const master = user.role === 'master_distributor';
  const scoped = master || Boolean(user.distributor);
  const previewAccount = Boolean(user.distributorPreviewExpiresAt);
  const [welcome, setWelcome] = useState(false);
  const welcomeCheck = useRef(null);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const inbox = admin;
  const [filter, setFilter] = useState('all');
  const [assignment, setAssignment] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const attachmentGeneration = useRef(0);
  const [actionBusy, setActionBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [newMessages, setNewMessages] = useState(false);
  const session = useRef(captureSession());
  const live = useRef(true);
  const listGeneration = useRef(0);
  const threadGeneration = useRef(0);
  const listPages = useRef(1);
  const selectedRef = useRef(null);
  selectedRef.current = selected;
  const messageRef = useRef([]);
  const threadBox = useRef();
  const nearBottom = useRef(true);
  const previewRef = useRef(null);
  const requestRef = useRef(null);
  const readProgress = useRef(new Map());
  const creatingRef = useRef(false);
  creatingRef.current = creating;
  const previewDialog = useRef();
  const previewTrigger = useRef();
  const valid = useCallback(() => live.current && isSessionCurrent(session.current), []);
  const acceptRequest = useCallback(
    (next) => {
      if (!valid() || next._id !== selectedRef.current) return;
      const previous = requestRef.current;
      if (previous?._id === next._id && previous.revision > next.revision) return;
      requestRef.current = next;
      setRequest(next);
    },
    [valid]
  );
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      cancelHelpReads();
      if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    };
  }, []);
  useEffect(() => {
    clearHelpDrafts(identity);
  }, [identity]);
  const date = (value) => helpDate(value, locale);
  const closePreview = () => {
    attachmentGeneration.current += 1;
    setAttachmentBusy(false);
    const wasOpen = Boolean(previewRef.current);
    if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    previewRef.current = null;
    setPreview(null);
    if (previewTrigger.current?.isConnected) previewTrigger.current.focus();
    if (wasOpen) requestAnimationFrame(() => markVisibleRead());
  };
  const previewKey = (event) => helpPreviewKey(event, previewDialog, closePreview);
  const loadConfig = useCallback(async () => {
    try {
      const result = await service.config();
      if (valid()) {
        setConfig(result);
        setConfigError(false);
      }
    } catch {
      if (valid()) setConfigError(true);
    }
  }, [valid]);
  useEffect(() => {
    if (admin || previewAccount || !config?.enabled || config?.readOnly) return;
    // Reuse the claim during StrictMode effect replay rather than consuming it twice.
    if (!welcomeCheck.current) welcomeCheck.current = service.welcome();
    welcomeCheck.current
      .then((result) => {
        if (valid() && result.showWelcome) setWelcome(true);
      })
      .catch(() => {});
  }, [admin, previewAccount, config?.enabled, config?.readOnly, valid]);
  const loadAssignees = useCallback(async () => {
    if (admin) {
      const result = await service.assignees();
      if (valid()) setAssignees(result.items);
    }
  }, [admin, valid]);
  useEffect(() => {
    loadConfig();
    loadAssignees().catch(() => {
      if (valid()) setError(t('Could not load support admins. Refresh to try again.'));
    });
  }, [loadAssignees, loadConfig, t, valid]);
  const denyAccess = useCallback(() => {
    threadGeneration.current += 1;
    listGeneration.current += 1;
    attachmentGeneration.current += 1;
    messageRef.current = [];
    requestRef.current = null;
    selectedRef.current = null;
    setMessages([]);
    setRequest(null);
    setSelected(null);
    setCreating(false);
    setRows([]);
    setTotal(0);
    setUnread(0);
    setCursor(null);
    setLoading(false);
    setThreadLoading(false);
    setAttachmentBusy(false);
    if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    previewRef.current = null;
    setPreview(null);
    clearHelpDrafts();
    setError(t('This conversation is no longer available to your account.'));
  }, [t]);
  const loadList = useCallback(
    async (next = null) => {
      const generation = ++listGeneration.current;
      const count = next ? 1 : listPages.current;
      let nextPage = next;
      let result;
      const items = [];
      for (let page = 0; page < count; page += 1) {
        try {
          result = await service.search(
            {
              status: filter,
              ...(inbox ? { assignment } : {}),
              search,
              cursor: nextPage,
              limit: 25,
            },
            inbox
          );
        } catch (error) {
          if (
            valid() &&
            generation === listGeneration.current &&
            [401, 403, 404].includes(error.response?.status)
          ) {
            denyAccess();
            return;
          }
          throw error;
        }
        if (!valid() || generation !== listGeneration.current) return;
        items.push(...result.items);
        nextPage = result.nextCursor;
        if (!nextPage) break;
      }
      if (!valid() || generation !== listGeneration.current) return;
      if (next) listPages.current += 1;
      setRows((old) =>
        Array.from(new Map([...(next ? old : []), ...items].map((row) => [row._id, row])).values())
      );
      setCursor(result.nextCursor);
      setTotal(result.total);
      setUnread(result.unreadCount);
      setLoading(false);
      if (!search && filter === 'all' && (!inbox || assignment === 'all'))
        window.dispatchEvent(
          new CustomEvent('support:unread', { detail: { identity, count: result.unreadCount } })
        );
    },
    [assignment, filter, inbox, search, valid, denyAccess, identity]
  );
  const refreshListRef = useRef(loadList);
  refreshListRef.current = loadList;
  useEffect(() => {
    listPages.current = 1;
    setLoading(true);
    setRows([]);
    setCursor(null);
    loadList().catch(() => {
      if (valid()) {
        setLoading(false);
        setError(t('Could not load requests. Refresh to try again.'));
      }
    });
  }, [loadList, t, valid]);
  const markVisibleRead = useCallback(async () => {
    const id = selectedRef.current;
    const sequence = messageRef.current.at(-1)?.sequence || 0;
    const node = threadBox.current;
    if (
      previewAccount ||
      config?.readOnly ||
      !valid() ||
      !id ||
      !sequence ||
      !node ||
      !nearBottom.current ||
      previewRef.current ||
      node.scrollHeight - node.scrollTop - node.clientHeight > 2 ||
      document.visibilityState === 'hidden'
    )
      return;
    const progress = readProgress.current.get(id) || { acknowledged: 0, pending: 0 };
    if (sequence <= Math.max(progress.acknowledged, progress.pending)) return;
    progress.pending = sequence;
    readProgress.current.set(id, progress);
    try {
      await service.read(id, sequence);
      if (valid()) {
        progress.acknowledged = Math.max(progress.acknowledged, sequence);
        setRows((old) =>
          old.map((row) =>
            row._id === id && row.lastSequence <= sequence ? { ...row, unreadCount: 0 } : row
          )
        );
      }
    } catch {
      /* A failed receipt never changes delivery state; retry on the next visible refresh. */
    } finally {
      if (progress.pending === sequence) progress.pending = 0;
    }
  }, [valid, previewAccount, config?.readOnly]);
  const scrollEnd = useCallback(() => {
    if (threadBox.current) threadBox.current.scrollTop = threadBox.current.scrollHeight;
    nearBottom.current = true;
    setNewMessages(false);
    markVisibleRead();
  }, [markVisibleRead]);
  const loadThread = useCallback(
    async (id, initial = false) => {
      const generation = threadGeneration.current;
      try {
        const summary = await service.request(id);
        if (!valid() || generation !== threadGeneration.current || selectedRef.current !== id)
          return;
        let after = initial ? 0 : messageRef.current.at(-1)?.sequence || 0;
        let incoming = [];
        let more = true;
        while (more) {
          const page = await service.messages(id, after);
          if (!valid() || generation !== threadGeneration.current || selectedRef.current !== id)
            return;
          incoming = [...incoming, ...page.items];
          if (!page.hasMore) more = false;
          else if (!page.nextAfter || page.nextAfter <= after)
            throw new Error('Invalid message cursor');
          else after = page.nextAfter;
        }
        if (!valid() || generation !== threadGeneration.current || selectedRef.current !== id)
          return;
        acceptRequest(summary);
        const combined = mergeMessages(messageRef.current, incoming);
        messageRef.current = combined;
        setMessages(combined);
        setThreadLoading(false);
        if (incoming.length && !initial && !nearBottom.current) setNewMessages(true);
        if ((initial || nearBottom.current) && incoming.length)
          requestAnimationFrame(() => {
            if (
              valid() &&
              generation === threadGeneration.current &&
              selectedRef.current === id &&
              document.visibilityState !== 'hidden' &&
              nearBottom.current
            )
              scrollEnd();
          });
        else if (nearBottom.current) markVisibleRead();
      } catch (error) {
        if (
          valid() &&
          generation === threadGeneration.current &&
          selectedRef.current === id &&
          [401, 403, 404].includes(error.response?.status)
        ) {
          denyAccess();
          return;
        }
        throw error;
      }
    },
    [valid, acceptRequest, markVisibleRead, scrollEnd, denyAccess]
  );
  useEffect(() => {
    threadGeneration.current += 1;
    messageRef.current = [];
    requestRef.current = null;
    nearBottom.current = true;
    setMessages([]);
    setRequest(null);
    setNewMessages(false);
    if (!selected) return;
    setThreadLoading(true);
    loadThread(selected, true).catch(() => {
      if (valid()) {
        setThreadLoading(false);
        setError(t('Could not load this conversation. Refresh to try again.'));
      }
    });
  }, [selected, loadThread, t, valid]);
  const refresh = useCallback(async () => {
    await Promise.all([
      loadList(),
      selectedRef.current ? loadThread(selectedRef.current) : Promise.resolve(),
      loadConfig(),
      loadAssignees(),
    ]);
  }, [loadList, loadThread, loadConfig, loadAssignees]);
  useEffect(() => {
    let stopped = false;
    let timer;
    let failures = 0;
    let running = false;
    const poll = async () => {
      if (running || stopped) return;
      clearTimeout(timer);
      if (document.visibilityState === 'hidden') return;
      running = true;
      try {
        await refresh();
        failures = 0;
        if (valid())
          setError((previous) =>
            previous ===
            t('Updates paused. Check your connection or refresh. Your draft is preserved.')
              ? ''
              : previous
          );
      } catch {
        failures += 1;
        if (valid())
          setError(t('Updates paused. Check your connection or refresh. Your draft is preserved.'));
      } finally {
        running = false;
        if (!stopped) timer = setTimeout(poll, Math.min(160000, 20000 * 2 ** failures));
      }
    };
    timer = setTimeout(poll, 20000);
    const resume = () => {
      if (document.visibilityState !== 'hidden') poll();
      else clearTimeout(timer);
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [refresh, t, valid]);
  const openRequest = (id) => {
    closePreview();
    setCreating(false);
    setSettings(false);
    setError('');
    setNotice('');
    setSelected(id);
  };
  const send = async (body) => {
    const wasCreating = creating;
    const id = selected;
    let result;
    try {
      result = wasCreating ? await service.create(body) : await service.reply(id, body);
    } catch (error) {
      if (valid() && [401, 403, 404].includes(error.response?.status)) denyAccess();
      throw error;
    }
    if (!valid()) return;
    if (wasCreating) {
      if (creatingRef.current) {
        setCreating(false);
        setSelected(result.request._id);
        setNotice(t('Request received. You can follow replies here.'));
      } else setNotice(t('Request {reference} received.', { reference: result.request.reference }));
    } else if (selectedRef.current === id) {
      acceptRequest(result.request);
      messageRef.current = mergeMessages(messageRef.current, [result.message]);
      setMessages(messageRef.current);
      const generation = threadGeneration.current;
      requestAnimationFrame(() => {
        if (valid() && selectedRef.current === id && generation === threadGeneration.current)
          scrollEnd();
      });
    }
    // A refresh failure must never turn a confirmed send into a failed send.
    refreshListRef.current().catch(() => {});
  };
  const mutate = async (fn) => {
    const id = selected;
    setActionBusy(true);
    setError('');
    try {
      const result = await fn();
      if (valid() && selectedRef.current === id) {
        acceptRequest(result);
        refreshListRef.current().catch(() => {});
      }
    } catch (err) {
      if (valid() && [401, 403, 404].includes(err.response?.status)) {
        denyAccess();
        return;
      }
      if (valid() && selectedRef.current === id)
        setError(
          t(
            err.response?.status === 409
              ? 'This request changed. Refresh the conversation before trying again. Your draft is preserved.'
              : 'Could not save this change. Refresh and try again.'
          )
        );
    } finally {
      if (valid()) setActionBusy(false);
    }
  };
  const attachment = async (file) => {
    setError('');
    setAttachmentBusy(true);
    const generation = ++attachmentGeneration.current;
    const trigger = document.activeElement;
    try {
      const id = selected;
      const blob = await service.attachment(id, file._id);
      if (!valid() || selectedRef.current !== id || generation !== attachmentGeneration.current)
        return;
      if (
        !(blob instanceof Blob) ||
        !['image/jpeg', 'image/png', 'application/pdf'].includes(blob.type) ||
        blob.type !== file.mimeType ||
        blob.size < 1 ||
        blob.size > 5242880
      )
        throw new Error('Invalid attachment');
      closePreview();
      previewTrigger.current = trigger;
      const result = { ...file, url: URL.createObjectURL(blob) };
      previewRef.current = result;
      setPreview(result);
    } catch (error) {
      if (valid() && generation === attachmentGeneration.current) {
        if ([401, 403, 404].includes(error.response?.status)) {
          closePreview();
          setRequest(null);
          setMessages([]);
          messageRef.current = [];
          setSelected(null);
          clearHelpDrafts();
          setError(t('This conversation is no longer available to your account.'));
        } else setError(t('Could not open this attachment. Please try again.'));
      }
    } finally {
      if (valid() && generation === attachmentGeneration.current) setAttachmentBusy(false);
    }
  };
  const contact = config?.contact;
  const writesEnabled =
    !previewAccount &&
    !config?.readOnly &&
    (scoped ? config?.writesEnabled === true : config?.writesEnabled !== false);
  const canCreate =
    writesEnabled &&
    config?.enabled &&
    (scoped ? config?.canCreate === true : config?.canCreate !== false);
  const canReply = writesEnabled && (request?.permissions?.canReply ?? !scoped);
  const canChangeStatus = writesEnabled && (request?.permissions?.canChangeStatus ?? !scoped);
  const canAssign = admin && writesEnabled && (request?.permissions?.canAssign ?? true);
  const rosterTitle = inbox ? 'Support inbox' : master ? 'Distributor requests' : 'My requests';
  return (
    <HelpWorkspaceView
      model={{
        t,
        admin,
        master,
        config,
        configError,
        settings,
        setSettings,
        setNotice,
        setCreating,
        setSelected,
        closePreview,
        canCreate,
        welcome,
        setWelcome,
        previewAccount,
        scoped,
        error,
        setError,
        refresh,
        notice,
        setConfig,
        selected,
        creating,
        identity,
        send,
        request,
        actionBusy,
        canAssign,
        assignees,
        mutate,
        canChangeStatus,
        user,
        threadBox,
        nearBottom,
        setNewMessages,
        markVisibleRead,
        threadLoading,
        messages,
        date,
        attachmentBusy,
        attachment,
        newMessages,
        scrollEnd,
        canReply,
        contact,
        preview,
        previewDialog,
        previewKey,
      }}
      listProps={{
        t,
        inbox,
        master,
        admin,
        rosterTitle,
        unread,
        total,
        searchInput,
        setSearchInput,
        search,
        setSearch,
        filter,
        setFilter,
        assignment,
        setAssignment,
        loading,
        rows,
        selected,
        openRequest,
        date,
        cursor,
        loadList,
        setError,
      }}
    />
  );
}

export default function HelpCenter() {
  const { user, sessionVersion } = useAuth();
  if (
    !user ||
    user.isActive === false ||
    !['admin', 'sales_rep', 'clinic', 'master_distributor'].includes(user.role)
  )
    return null;
  const identity = helpIdentity(user, sessionVersion);
  return <HelpWorkspace key={identity} identity={identity} user={user} />;
}
